import * as React from 'react';
import { ListResponseSmartParkAPI } from 'src/lib/queries';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import { useGetActiveStayPeriod } from 'src/features/stay/api/useGetActiveStayPeriod';
import { useGetPastStayPeriod } from 'src/features/stay/api/useGetPastStayPeriod';
import { ActiveStayPeriod, STAY_TYPE, VISITOR_CATEGORY } from 'src/features/stay/types';
import { formatTimestampToCustomId } from 'src/features/visitor/utils/timestampToId';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalMessages } from 'src/registry/utils/messages';
import SidePanelTabs from 'src/features/stay/components/sidePanel/SidePanelTabs';
import OverviewStayPeriod from 'src/features/stay/components/dashboard/OverviewStayPeriod';
import ZonesStayPeriod from 'src/features/stay/components/dashboard/ZonesStayPeriod';
import { useListGatewayPasses } from 'src/features/gatewayPass/api/useListGatewayPasses';
import ImageCarousel from 'src/features/visitor/components/carousel/ImageCarousel';
import { GatewayPass } from 'src/features/gatewayPass/types';
import { useParking } from 'src/providers/ParkingProvider';
import { visitorCategoryBadgeOptions } from 'src/features/visitor/utils/constants';

type StayPeriodSidePanelProps = {
    selectedStayId?: string;
    periodId?: string;
    stayType?: STAY_TYPE;
    setSelectedZoneGatewayPass: React.Dispatch<React.SetStateAction<string>>;
};

const StayPeriodSidePanel = ({
    selectedStayId,
    periodId,
    stayType,
    setSelectedZoneGatewayPass,
}: StayPeriodSidePanelProps) => {
    const { formatMessage, locale } = useIntl();

    const { parkingId } = useParking();
    const { data: activeStayPeriods } = useGetActiveStayPeriod({
        parkingId,
        stayId: selectedStayId ?? '',
        periodId: periodId ?? '',
        config: { enabled: (!!selectedStayId || !!periodId) && stayType === STAY_TYPE.ACTIVE },
    });

    const { data: pastStayPeriods } = useGetPastStayPeriod({
        parkingId,
        stayId: selectedStayId ?? '',
        periodId: periodId ?? '',
        config: { enabled: (!!selectedStayId || !!periodId) && stayType === STAY_TYPE.PAST },
    });

    const stayData = (stayType === STAY_TYPE.ACTIVE ? activeStayPeriods : pastStayPeriods) as ActiveStayPeriod;

    const { data: listGatewayPassesActiveStay } = useListGatewayPasses({
        parkingId,
        active_stay_period: periodId ?? '',
        config: { enabled: !!periodId && stayType === STAY_TYPE.ACTIVE },
    });

    const { data: listGatewayPassesPastStay } = useListGatewayPasses({
        parkingId,
        past_stay_period: periodId ?? '',
        config: { enabled: !!periodId && stayType === STAY_TYPE.PAST },
    });

    const gatewayPasses = (
        stayType === STAY_TYPE.ACTIVE ? listGatewayPassesActiveStay : listGatewayPassesPastStay
    ) as ListResponseSmartParkAPI<GatewayPass>;

    const identifiers =
        gatewayPasses?.results[0]?.identifiers?.map((identifier) => `${identifier.value}`)?.join(', ') ?? '-';

    const visitorCategoryBadge = () => {
        if (!stayData) return null;
        const category = stayData?.stay.period_visitor_category as VISITOR_CATEGORY;
        return (
            <Badge theme={visitorCategoryBadgeOptions[category].theme}>
                <FontAwesomeIcon icon={visitorCategoryBadgeOptions[category].icon as IconProp} />
                {formatMessage(visitorCategoryBadgeOptions[category].label)}
            </Badge>
        );
    };

    const gatewayPassesPhotos = React.useMemo(() => {
        return gatewayPasses?.results?.map((pass) => {
            return {
                image: pass.photo,
                title: pass.gateway?.translations.find((z) => z.language === locale)?.name,
                subtitle: `${formatMessage(visitorMessages.pass)} ${formatTimestampToCustomId(pass.time)}`,
            };
        });
    }, [gatewayPasses, locale, formatMessage]);

    return (
        <>
            {stayData ? (
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle className="pr-0 font-mono">
                            {formatMessage(visitorMessages.stayPeriod)}
                        </DialogTitle>
                        {visitorCategoryBadge()}
                    </div>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-5 w-60" />
                </DialogHeader>
            )}

            <DialogSeparator />
            <DialogBody>
                <ImageCarousel imageSlides={gatewayPassesPhotos} />
                <SidePanelTabs
                    tabs={[
                        {
                            key: 'overview',
                            label: formatMessage(visitorMessages.overview),
                            content: <OverviewStayPeriod identifiers={identifiers} stayData={stayData} />,
                        },
                        {
                            key: 'zones',
                            label: formatMessage(visitorMessages.zones),
                            content: (
                                <ZonesStayPeriod
                                    zoneStayPeriod={stayData?.zone_stay_periods}
                                    setSelectedZoneGatewayPass={setSelectedZoneGatewayPass}
                                />
                            ),
                        },
                    ]}
                    defaultActiveKey="overview"
                />
            </DialogBody>
            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
};

export default StayPeriodSidePanel;
